import React, { useState, useEffect } from "react";
import { Route, Redirect } from "react-router-dom";
import { Spin } from "antd";
import dayjs from "dayjs";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import FeatureErrorBoundary from "../FeatureErrorBoundary";
import db from "../../database";
import useSessionStore from "../../stores/useSessionStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";

// Only routes that read from db.tillEvents for core operations get this guard.
// Add a path here only when that screen breaks without an open till event.
const TILL_EVENT_ROUTES = ["/pos"];

const PrivateRoute = ({ component: Component, errorBoundary, ...rest }) => {
  const authHeaders = getOAuthHeaders();
  const cleanToken = authHeaders?.access_token;
  const email = authHeaders?.email;
  const username = authHeaders?.username;

  const isAuthenticated = cleanToken !== "bearer undefined" && (email || username);

  const needsTillCheck = isAuthenticated && TILL_EVENT_ROUTES.some((p) => rest.path?.startsWith(p));

  // "checking" → spinner while verifying IndexedDB open event
  // "ok"          → proceed to render normally
  // "/till"       → stale Zustand session, no open event in IndexedDB
  // "/close-till" → open event exists but from a previous business day
  const [tillCheck, setTillCheck] = useState(needsTillCheck ? "checking" : "ok");

  useEffect(() => {
    if (!needsTillCheck) {
      setTillCheck("ok");
      return;
    }

    const verify = async () => {
      try {
        const posConfig = usePOSConfigStore.getState().posConfig;
        const shouldCheckTill =
          posConfig?.enableProduction !== "Y" &&
          posConfig?.enableProductionRMIssue !== "Y" &&
          posConfig?.enableEstimate !== "Y";

        if (!shouldCheckTill) {
          setTillCheck("ok");
          return;
        }

        const tillSession = useSessionStore.getState().tillSession;
        if (!tillSession || tillSession.tillStatus !== "open") {
          // No active session — PointOfSaleCore handles the /till redirect
          setTillCheck("ok");
          return;
        }

        // Verify the open event actually exists in IndexedDB.
        // Zustand persists across restarts; IndexedDB can diverge (server sync
        // closed the event, DB cleared, tab restored with stale state, etc.).
        // Use toArray() + Array.find() instead of .filter() — closures are lost via IPC in SQLite mode.
        const allSessionEvents = await db.tillEvents
          .where("tillSessionId")
          .equals(tillSession.tillSessionId)
          .toArray();
        const openEvents = allSessionEvents.filter((e) => e.tillStatus === "open");

        if (openEvents.length === 0) {
          // Zustand says open but no matching open event in IndexedDB — clear stale state
          useSessionStore.getState().setTillSession(null);
          setTillCheck("/till");
          return;
        }

        // Open event exists — check if it is from a previous business day
        const tillDate = openEvents[0].openingTime?.split(" ")[0];
        if (tillDate && tillDate !== dayjs().format("YYYY-MM-DD")) {
          setTillCheck("/close-till");
          return;
        }

        setTillCheck("ok");
      } catch (err) {
        console.error("[PrivateRoute] Till event verification failed:", err);
        // Fail open — never block the user due to a DB read error
        setTillCheck("ok");
      }
    };

    verify();
  }, [needsTillCheck]);

  if (tillCheck === "checking") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (tillCheck === "/till" || tillCheck === "/close-till") {
    return <Route {...rest} render={() => <Redirect to={tillCheck} />} />;
  }

  return (
    <>
      <Route
        {...rest}
        render={(props) =>
          isAuthenticated ? (
            errorBoundary ? (
              <FeatureErrorBoundary name={errorBoundary}>
                <Component {...props} {...rest} />
              </FeatureErrorBoundary>
            ) : (
              <Component {...props} {...rest} />
            )
          ) : (
            <Redirect
              to={{
                pathname: "/sign-in",
                state: { from: props.location },
              }}
            />
          )
        }
      />
    </>
  );
};

export default PrivateRoute;
